"use client";


import { useTranslation } from "@/src/i18n/client";
import { Link } from "@tanstack/react-router";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Footer } from "./layout/Footer";
import {
  UploadIcon,
  LightningIcon,
  FileCodeIcon,
  CpuIcon,
  ShieldCheckIcon,
  PlugsIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  DownloadIcon,
  EnvelopeSimpleIcon,
  ClockIcon,
  FolderSimpleIcon,
  CaretDownIcon,
} from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";

export function LandingPage() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const sections = [
    { id: 'hero', label: 'Hero' },
    { id: 'how-it-works', label: 'How It Works' },
    { id: 'upload-efficiency', label: 'Upload' },
    { id: 'email-flow', label: 'Email' },
    { id: 'features', label: 'Features' },
    { id: 'cta', label: 'CTA' },
  ];

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const sectionHeight = container.scrollHeight / sections.length;
      const currentSection = Math.round(scrollTop / sectionHeight);
      setActiveSection(Math.min(currentSection, sections.length - 1));
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [sections.length]);

  const scrollToSection = (index: number) => {
    const container = containerRef.current;
    if (!container) return;
    
    const sectionHeight = container.scrollHeight / sections.length;
    container.scrollTo({
      top: sectionHeight * index,
      behavior: 'smooth',
    });
  };


  return (
    <div ref={containerRef} className="relative h-[calc(100vh-4rem)] overflow-x-hidden overflow-y-auto snap-y snap-mandatory scroll-smooth scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 bg-[url('/bg.png')] bg-cover bg-center opacity-30 dark:opacity-20 dark:invert"
      />
      {/* Hero Section */}
      <section id="hero" className="min-h-[calc(100vh-4rem)] snap-start flex items-center px-4 py-8 sm:py-12">
        <div className="max-w-5xl mx-auto text-center w-full">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 sm:mb-6 px-2">
            {t("landing.title")}
            <br />
            <span className="text-blue-500">{t("landing.titleHighlight")}</span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-xl mx-auto mb-8 sm:mb-10 px-4">
            {t("landing.subtitle")}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4">
            <Button
              nativeButton={false}
              render={<Link to="/upload" />}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 sm:px-8 py-3 rounded-lg flex items-center gap-2 h-11 sm:h-12 text-sm sm:text-base w-full sm:w-auto"
            >
              {t("landing.startAnalyzing")}
              <ArrowRightIcon className="w-4 h-4" />
            </Button>
            <Button
              nativeButton={false}
              render={<Link to="/register" />}
              variant="outline"
              className="px-6 sm:px-8 py-3 rounded-lg h-11 sm:h-12 text-sm sm:text-base w-full sm:w-auto"
            >
              {t("landing.signUp")}
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="min-h-[calc(100vh-4rem)] snap-start flex items-center px-4 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto w-full">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 sm:mb-12 text-center px-2">
            {t("landing.howItWorksTitle")}
          </h2>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            <StepCard
              number={1}
              icon={<UploadIcon className="w-6 h-6 sm:w-8 sm:h-8" />}
              title={t("landing.step1Title")}
              description={t("landing.step1Desc")}
            />
            <StepCard
              number={2}
              icon={<CpuIcon className="w-6 h-6 sm:w-8 sm:h-8" />}
              title={t("landing.step2Title")}
              description={t("landing.step2Desc")}
            />
            <StepCard
              number={3}
              icon={<DownloadIcon className="w-6 h-6 sm:w-8 sm:h-8" />}
              title={t("landing.step3Title")}
              description={t("landing.step3Desc")}
            />
          </div>
        </div>
      </section>

      {/* Upload Efficiency Section */}
      <section id="upload-efficiency" className="min-h-[calc(100vh-4rem)] snap-start flex items-center px-4 py-8 sm:py-12">
        <div className="max-w-5xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="px-2">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
                {t("landing.uploadEfficiencyTitle")}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8">
                {t("landing.uploadEfficiencyDesc")}
              </p>
              <div className="space-y-3 sm:space-y-4">
                <BenefitItem text={t("landing.uploadBenefit1")} />
                <BenefitItem text={t("landing.uploadBenefit2")} />
                <BenefitItem text={t("landing.uploadBenefit3")} />
              </div>
            </div>
            <div className="grid gap-3 sm:gap-4">
              <div className="bg-card/70 border border-blue-500/20 rounded-xl p-4 sm:p-6 flex items-start gap-3 sm:gap-4">
                <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 mt-1 shrink-0" />
                <div>
                  <h3 className="font-semibold text-sm sm:text-base mb-1">{t("landing.uploadCard1Title")}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">{t("landing.uploadCard1Desc")}</p>
                </div>
              </div>
              <div className="bg-card/70 border border-blue-500/20 rounded-xl p-4 sm:p-6 flex items-start gap-3 sm:gap-4">
                <FolderSimpleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 mt-1 shrink-0" />
                <div>
                  <h3 className="font-semibold text-sm sm:text-base mb-1">{t("landing.uploadCard2Title")}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">{t("landing.uploadCard2Desc")}</p>
                </div>
              </div>
              <div className="bg-card/70 border border-blue-500/20 rounded-xl p-4 sm:p-6 flex items-start gap-3 sm:gap-4">
                <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 mt-1 shrink-0" weight="fill" />
                <div>
                  <h3 className="font-semibold text-sm sm:text-base mb-1">{t("landing.uploadCard3Title")}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">{t("landing.uploadCard3Desc")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mail Worker Flow Section */}
      <section id="email-flow" className="min-h-[calc(100vh-4rem)] snap-start flex items-center px-4 py-8 sm:py-12">
        <div className="max-w-5xl mx-auto w-full">
          <div className="rounded-2xl sm:rounded-3xl border border-blue-500/20 bg-linear-to-br from-blue-500/10 via-transparent to-cyan-500/10 p-6 sm:p-8 md:p-10">
            <div className="flex flex-col gap-4 sm:gap-6 mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold px-2">
                {t("landing.emailFlowTitle")}
              </h2>
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-blue-600 dark:text-blue-300 w-fit">
                {t("landing.emailFlowDesc")}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              <div className="rounded-xl sm:rounded-2xl border border-blue-500/20 bg-card/70 p-4 sm:p-6">
                <div className="mb-3 sm:mb-4 flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg sm:rounded-xl bg-blue-500/15 text-blue-500">
                  <EnvelopeSimpleIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <h3 className="font-semibold text-base sm:text-lg">{t("landing.emailFlowStep1Title")}</h3>
              </div>
              <div className="rounded-xl sm:rounded-2xl border border-blue-500/20 bg-card/70 p-4 sm:p-6">
                <div className="mb-3 sm:mb-4 flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg sm:rounded-xl bg-blue-500/15 text-blue-500">
                  <UploadIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <h3 className="font-semibold text-base sm:text-lg">{t("landing.emailFlowStep2Title")}</h3>
              </div>
              <div className="rounded-xl sm:rounded-2xl border border-blue-500/20 bg-card/70 p-4 sm:p-6">
                <div className="mb-3 sm:mb-4 flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg sm:rounded-xl bg-blue-500/15 text-blue-500">
                  <CpuIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <h3 className="font-semibold text-base sm:text-lg">{t("landing.emailFlowStep3Title")}</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="min-h-[calc(100vh-4rem)] snap-start flex items-center px-4 py-8 sm:py-12">
        <div className="max-w-5xl mx-auto w-full">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 sm:mb-12 text-center px-2">
            {t("landing.featuresTitle")}
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <FeatureCard
              icon={<LightningIcon className="w-5 h-5" />}
              title={t("landing.featureInstantTitle")}
              description={t("landing.featureInstantDesc")}
            />
            <FeatureCard
              icon={<FileCodeIcon className="w-5 h-5" />}
              title={t("landing.featureMachineTitle")}
              description={t("landing.featureMachineDesc")}
            />
            <FeatureCard
              icon={<ShieldCheckIcon className="w-5 h-5" />}
              title={t("landing.featureAccuracyTitle")}
              description={t("landing.featureAccuracyDesc")}
            />
            <FeatureCard
              icon={<PlugsIcon className="w-5 h-5" />}
              title={t("landing.featureApiTitle")}
              description={t("landing.featureApiDesc")}
            />
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 sm:p-6 flex flex-col justify-center">
              <p className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400 mb-1 sm:mb-2">
                {t("landing.benefit5")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("landing.benefit4")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Output Preview + CTA Section */}
      <section id="cta" className="min-h-[calc(100vh-4rem)] snap-start flex flex-col px-4 py-8 sm:py-12">
        <div className="flex-1 flex items-center">
          <div className="max-w-5xl mx-auto w-full">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div className="bg-white/90 dark:bg-zinc-950 rounded-xl sm:rounded-2xl border border-zinc-200/80 dark:border-zinc-800/80 shadow-[0_10px_30px_rgba(0,0,0,0.2)] sm:shadow-[0_20px_60px_rgba(0,0,0,0.25)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur order-1 lg:order-1 h-[380px] sm:h-[420px] overflow-hidden">
                <Tabs defaultValue="json" className="text-zinc-900 dark:text-zinc-200">
                  <div className="flex items-center justify-between px-3 sm:px-5 py-2 sm:py-3 border-b border-zinc-200/80 dark:border-zinc-800/80 bg-white/80 dark:bg-zinc-900/40">
                    <div className="flex gap-1.5 sm:gap-2">
                      <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500"></div>
                      <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
                    </div>
                    <TabsList className="mx-2 sm:mx-4" variant="default">
                      <TabsTrigger value="json" className="text-xs sm:text-sm">{t("landing.outputTabJson")}</TabsTrigger>
                      <TabsTrigger value="csv" className="text-xs sm:text-sm">{t("landing.outputTabCsv")}</TabsTrigger>
                      <TabsTrigger value="excel" className="text-xs sm:text-sm">{t("landing.outputTabExcel")}</TabsTrigger>
                    </TabsList>
                    <div className="text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400 hidden sm:block">GearLens Output</div>
                  </div>
                  <div className="p-4 sm:p-6 h-[calc(380px-45px)] sm:h-[calc(420px-52px)] overflow-auto">
                  <TabsContent value="json">
                    <pre className="text-xs sm:text-sm font-mono text-zinc-800 dark:text-zinc-300 overflow-x-auto">
                      <code>{`{
  "module": 2.5,
  "teeth_count": 24,
  "pressure_angle": 20.0,
  "helix_angle": 15.0,
  "face_width": 30.0,
  "quality_grade": "ISO 6",
  "material": "42CrMo4"
}`}</code>
                    </pre>
                  </TabsContent>
                  <TabsContent value="csv">
                    <pre className="text-xs sm:text-sm font-mono text-zinc-800 dark:text-zinc-300 overflow-x-auto">
                      <code>{t("landing.outputCsvSample")}</code>
                    </pre>
                  </TabsContent>
                  <TabsContent value="excel">
                    <div className="rounded-lg border border-zinc-200/80 dark:border-zinc-800/80 overflow-hidden">
                      <div className="bg-zinc-100 text-zinc-600 dark:bg-zinc-900/80 dark:text-zinc-300 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 border-b border-zinc-200/80 dark:border-zinc-800/80">Sheet: Gear</div>
                      <div className="grid grid-cols-[36px_1.2fr_0.8fr] sm:grid-cols-[44px_1.2fr_0.8fr]">
                        <div className="bg-zinc-50 dark:bg-zinc-900/60 border-b border-r border-zinc-200/80 dark:border-zinc-800/80" />
                        {['A', 'B'].map((col) => (
                          <div
                            key={col}
                            className="px-2 sm:px-3 py-1.5 sm:py-2 text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400 border-b border-r border-zinc-200/80 dark:border-zinc-800/80 bg-zinc-50 dark:bg-zinc-900/60"
                          >
                            {col}
                          </div>
                        ))}
                        {[
                          [1, 'module', '2.5'],
                          [2, 'teeth_count', '24'],
                          [3, 'pressure_angle', '20.0'],
                          [4, 'helix_angle', '15.0'],
                          [5, 'face_width', '30.0'],
                          [6, 'quality_grade', 'ISO 6'],
                          [7, 'material', '42CrMo4'],
                        ].map(([row, key, value]) => (
                          <div key={row} className="contents">
                            <div className="px-2 sm:px-3 py-1.5 sm:py-2 text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400 border-b border-r border-zinc-200/80 dark:border-zinc-800/80 bg-zinc-50 dark:bg-zinc-900/60">
                              {row}
                            </div>
                            <div className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-zinc-900 dark:text-zinc-200 border-b border-r border-zinc-200/80 dark:border-zinc-800/80">
                              {key}
                            </div>
                            <div className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-zinc-900 dark:text-zinc-200 border-b border-zinc-200/80 dark:border-zinc-800/80">
                              {value}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </TabsContent>
                </div>
                </Tabs>
              </div>

              <div className="order-2 lg:order-2 px-2">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">
                  {t("landing.ctaTitle")}
                </h2>
                <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                  <BenefitItem text={t("landing.benefit1")} />
                  <BenefitItem text={t("landing.benefit2")} />
                  <BenefitItem text={t("landing.benefit3")} />
                  <BenefitItem text={t("landing.benefit6")} />
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Button
                    nativeButton={false}
                    render={<Link to="/register" />}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 sm:px-8 py-3 rounded-lg flex items-center justify-center gap-2 h-11 sm:h-12 text-sm sm:text-base w-full sm:w-auto"
                  >
                    {t("landing.signUp")}
                    <ArrowRightIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    nativeButton={false}
                    render={<Link to="/upload" />}
                    variant="outline"
                    className="px-6 sm:px-8 py-3 rounded-lg h-11 sm:h-12 text-sm sm:text-base w-full sm:w-auto"
                  >
                    {t("landing.startAnalyzing")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <Footer />
      </section>

      {/* Floating Navigation Dots */}
      <div className="fixed right-4 sm:right-6 top-1/2 -translate-y-1/2 z-40 hidden md:flex flex-col gap-3">
        {sections.map((section, index) => (
          <button
            key={section.id}
            onClick={() => scrollToSection(index)}
            className={`group relative transition-all duration-300 ${
              activeSection === index ? 'scale-110' : 'scale-100'
            }`}
            aria-label={`Go to ${section.label} section`}
          >
            <div
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                activeSection === index
                  ? 'bg-blue-500 scale-110'
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/60'
              }`}
            />
            <span className="absolute right-5 top-1/2 -translate-y-1/2 bg-background/90 backdrop-blur px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-border">
              {section.label}
            </span>
          </button>
        ))}
      </div>

      {/* Scroll Down Indicator (only on first section) */}
      {activeSection === 0 && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-40 animate-bounce">
          <button
            onClick={() => scrollToSection(1)}
            className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Scroll down"
          >
            <span className="text-xs font-medium">Scroll</span>
            <CaretDownIcon className="w-5 h-5" weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="bg-card/70 border border-blue-500/20 rounded-xl p-4 sm:p-6 hover:border-blue-500/30 transition-colors">
      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center mb-3 sm:mb-4">
        {icon}
      </div>
      <h3 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2">{title}</h3>
      <p className="text-muted-foreground text-xs sm:text-sm">{description}</p>
    </div>
  );
}

function StepCard({
  number,
  icon,
  title,
  description,
}: {
  number: number;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="text-center">
      <div className="relative inline-flex mb-4 sm:mb-6">
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-500">
          {icon}
        </div>
        <div className="absolute -bottom-1.5 -right-1.5 sm:-bottom-2 sm:-right-2 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-500 text-white text-xs sm:text-sm font-bold flex items-center justify-center">
          {number}
        </div>
      </div>
      <h3 className="font-semibold text-lg sm:text-xl mb-1 sm:mb-2">{title}</h3>
      <p className="text-muted-foreground text-xs sm:text-sm">{description}</p>
    </div>
  );
}

function BenefitItem({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 sm:gap-3">
      <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 shrink-0" weight="fill" />
      <span className="text-sm sm:text-base">{text}</span>
    </div>
  );
}
